/**
 * GraphQL query strings for autofix MCP tools.
 *
 * These target the mcp_* query fields on the Sailfish backend, which
 * require JWT auth and scope data to the authenticated user's company.
 */

export const MCP_GET_BACKEND_LOGS = `
  query McpGetBackendLogs($input: BackendLogsInput!, $companyId: Int) {
    mcpGetBackendLogs(input: $input, companyId: $companyId) {
      sessionId
      totalCount
      logs {
        level
        statement
        formattedTimestamp
      }
    }
  }
`;

export const MCP_GET_EXCEPTIONS = `
  query McpGetExceptions($input: MultiSessionInput!, $companyId: Int) {
    mcpGetExceptions(input: $input, companyId: $companyId) {
      sessionId
      totalCount
      exceptions {
        id
        timestampMs
        exceptionMessage
        file
        line
        column
        function
        code
        traceStack {
          file
          line
          function
          code
          offender
        }
      }
    }
  }
`;

export const MCP_GET_FRONTEND_LOGS = `
  query McpGetFrontendLogs($input: FrontendLogsInput!, $companyId: Int) {
    mcpGetFrontendLogs(input: $input, companyId: $companyId) {
      sessionId
      totalCount
      logs {
        level
        trace
        payload
        formattedTimestamp
      }
    }
  }
`;

export const MCP_GET_NETWORK_REQUEST_LOGS = `
  query McpGetNetworkRequestLogs($input: NetworkRequestsInput!, $companyId: Int) {
    mcpGetNetworkRequestLogs(input: $input, companyId: $companyId) {
      sessionId
      totalCount
      requests {
        url
        method
        responseCode
        success
        error
        formattedTimestampStart
        formattedTimestampEnd
        requestId
        hasGraphqlErrors
        durationMs
        graphqlOperationName
        graphqlOperationType
        responseBody
        requestBody
      }
    }
  }
`;

export const MCP_GET_NETWORK_HOPS = `
  query McpGetNetworkHops($input: NetworkHopsInput!, $companyId: Int) {
    mcpGetNetworkHops(input: $input, companyId: $companyId) {
      sessionId
      totalCount
      hops {
        line
        column
        name
        formattedTimestamp
        entrypoint
      }
    }
  }
`;

export const MCP_GET_PRINT_STATEMENTS = `
  query McpGetPrintStatements($input: PrintStatementsInput!, $companyId: Int) {
    mcpGetPrintStatements(input: $input, companyId: $companyId) {
      sessionId
      totalCount
      printStatements {
        output
        statement
        formattedTimestamp
      }
    }
  }
`;

export const MCP_GET_USER_ACTIONS = `
  query McpGetUserActions($input: UserActionsInput!, $companyId: Int) {
    mcpGetUserActions(input: $input, companyId: $companyId) {
      sessionId
      pageVisitUserActions {
        userActions {
          actionType
          textContent
          dotPath
          timestamp
        }
      }
    }
  }
`;

export const MCP_GET_FRONTEND_DISPLAY_DATA = `
  query McpGetFrontendDisplayData($input: MultiSessionInput!, $companyId: Int) {
    mcpGetFrontendDisplayData(input: $input, companyId: $companyId) {
      sessionId
      displayData {
        pageVisitId
        compactJson
      }
    }
  }
`;

export const MCP_GET_ISSUE_OVERVIEW = `
  query McpGetIssueOverview($issueId: Int!, $companyId: Int) {
    mcpGetIssueOverview(issueId: $issueId, companyId: $companyId) {
      issueId
      issueTitle
      totalOccurrences
      description
      aiGeneratedDescription
      customerSummary
      customerConversation
      firstSeen
      lastSeen
      exceptionMessage
      location
      stackFrames {
        file
        line
        function
        code
        offender
      }
      sessionIds
      wasCaught
      startTimestampMs
      endTimestampMs
    }
  }
`;

export const MCP_UPDATE_AUTOFIX_RUN = `
  mutation McpUpdateAutofixRun($runId: String!, $state: String, $logs: String, $branchUrl: String, $prUrl: String, $prProvider: String, $agentTrace: JSON, $companyId: Int) {
    mcpUpdateAutofixRun(runId: $runId, state: $state, logs: $logs, branchUrl: $branchUrl, prUrl: $prUrl, prProvider: $prProvider, agentTrace: $agentTrace, companyId: $companyId) {
      id
      runId
      issueId
      state
      source
      logs
      branchUrl
      prUrl
      prProvider
    }
  }
`;

export const MCP_CREATE_AUTOFIX_RUN = `
  mutation McpCreateAutofixRun($issueId: Int!, $runId: String!, $runSource: String, $companyId: Int) {
    mcpCreateDesktopAutofixRun(issueId: $issueId, runId: $runId, runSource: $runSource, companyId: $companyId) {
      id
      runId
      issueId
      state
      source
    }
  }
`;

export const MCP_CREATE_PULL_REQUEST = `
  mutation McpCreatePullRequest($runId: String!, $repoOwner: String!, $repoName: String!, $headBranch: String!, $title: String!, $body: String, $baseBranch: String, $companyId: Int) {
    mcpCreatePullRequest(runId: $runId, repoOwner: $repoOwner, repoName: $repoName, headBranch: $headBranch, title: $title, body: $body, baseBranch: $baseBranch, companyId: $companyId) {
      id
      runId
      issueId
      state
      prUrl
      prProvider
    }
  }
`;

export const DOWNLOAD_ARTIFACT = `
  mutation DownloadArtifact($branch: String, $artifactId: String!, $artifactType: String!, $companyId: Int) {
    downloadArtifact(branch: $branch, artifactId: $artifactId, artifactType: $artifactType, companyId: $companyId) {
      data
      branch
      artifactId
      artifactType
      gcsPath
      size
    }
  }
`;

export const GET_LATEST_AUTOFIX_RUN_BY_ISSUE_ID = `
  query GetLatestAutofixRunByIssueId($issueId: Int!, $companyId: Int) {
    getLatestAutofixRunByIssueId(issueId: $issueId, companyId: $companyId) {
      runId
      state
      branchUrl
      prUrl
    }
  }
`;

export const MCP_GET_COMPANY_REPOS = `
  query McpGetCompanyRepos($companyId: Int) {
    mcpGetCompanyRepos(companyId: $companyId) {
      gitOrg
      gitRepo
      gitProvider
    }
  }
`;

export const MCP_GET_CAPTURED_FUNCTION_SPANS = `
  query McpGetCapturedFunctionSpans($sessionId: String!, $companyId: Int, $limit: Int, $functionName: String, $filePath: String) {
    mcpGetCapturedFunctionSpans(sessionId: $sessionId, companyId: $companyId, limit: $limit, functionName: $functionName, filePath: $filePath) {
      id
      timestamp
      filePath
      functionName
      arguments
      returnValue
      durationNs
      sessionId
      serviceUuid
    }
  }
`;
